import React, { FC, useCallback, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ActivityType } from "@models/activity-type";
import {
  CognitiveDomainExport,
  exportPatientConversations,
  exportPatientGames,
  EXPORT_ACTIVE_GAME_IDS,
  EXPORT_DOMAIN_KEYS,
  EXPORT_GAME_I18N_KEYS,
} from "@service/patientExport.service";
import { triggerBrowserDownload } from "@utils/download.util";

export type PatientExportKind = "games" | "conversations";

export interface PatientExportDialogProps {
  open: boolean;
  kind: PatientExportKind;
  patientIds: string[];
  onClose: () => void;
  onError: (message: string) => void;
}

const ACTIVITY_TYPES = Object.values(ActivityType);

const PatientExportDialog: FC<PatientExportDialogProps> = ({
  open,
  kind,
  patientIds,
  onClose,
  onError,
}) => {
  const { t } = useTranslation();
  const defaultTo = DateTime.now().toISODate() ?? "";
  const defaultFrom =
    DateTime.now().minus({ days: 30 }).toISODate() ?? defaultTo;

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [domain, setDomain] = useState<CognitiveDomainExport | "">("");
  const [gameId, setGameId] = useState("");
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [includeTranscript, setIncludeTranscript] = useState(false);
  const [loading, setLoading] = useState(false);

  const title = useMemo(
    () =>
      kind === "games"
        ? t("patientManage.export.gamesTitle")
        : t("patientManage.export.conversationsTitle"),
    [kind, t],
  );

  const handleExport = useCallback(async () => {
    if (!patientIds.length) {
      return;
    }
    setLoading(true);
    try {
      const fromIso = DateTime.fromISO(from).startOf("day").toISO()!;
      const toIso = DateTime.fromISO(to).endOf("day").toISO()!;
      if (kind === "games") {
        const { blob, filename } = await exportPatientGames({
          patientIds,
          from: fromIso,
          to: toIso,
          ...(domain ? { domain } : {}),
          ...(gameId ? { gameId } : {}),
        });
        triggerBrowserDownload(blob, filename);
      } else {
        const { blob, filename } = await exportPatientConversations({
          patientIds,
          from: fromIso,
          to: toIso,
          ...(activityTypes.length
            ? { activityTypes }
            : {}),
          includeTranscript,
        });
        triggerBrowserDownload(blob, filename);
      }
      onClose();
    } catch (err) {
      onError(
        err instanceof Error ? err.message : t("patientManage.export.error"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    activityTypes,
    domain,
    from,
    gameId,
    includeTranscript,
    kind,
    onClose,
    onError,
    patientIds,
    t,
    to,
  ]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("patientManage.export.from")}
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label={t("patientManage.export.to")}
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {kind === "games" && (
            <>
              <FormControl fullWidth>
                <InputLabel id="export-domain-label">
                  {t("patientManage.export.domain")}
                </InputLabel>
                <Select
                  labelId="export-domain-label"
                  label={t("patientManage.export.domain")}
                  value={domain}
                  onChange={(e) =>
                    setDomain(e.target.value as CognitiveDomainExport | "")
                  }
                >
                  <MenuItem value="">
                    <em>{t("patientManage.export.allDomains")}</em>
                  </MenuItem>
                  {EXPORT_DOMAIN_KEYS.map((key) => (
                    <MenuItem key={key} value={key}>
                      {t(`games.sessionSummary.domains.${key}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="export-game-label">
                  {t("patientManage.export.gameType")}
                </InputLabel>
                <Select
                  labelId="export-game-label"
                  label={t("patientManage.export.gameType")}
                  value={gameId}
                  onChange={(e) => setGameId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t("patientManage.export.allGames")}</em>
                  </MenuItem>
                  {EXPORT_ACTIVE_GAME_IDS.map((id) => (
                    <MenuItem key={id} value={id}>
                      {t(
                        `games.library.${EXPORT_GAME_I18N_KEYS[id] ?? id}`,
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {kind === "conversations" && (
            <>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeTranscript}
                      onChange={(_, checked) => setIncludeTranscript(checked)}
                    />
                  }
                  label={t("patientManage.export.includeTranscript")}
                />
              </FormGroup>
              <FormControl fullWidth>
                <InputLabel id="export-activity-label">
                  {t("patientManage.export.activityTypes")}
                </InputLabel>
                <Select
                  labelId="export-activity-label"
                  label={t("patientManage.export.activityTypes")}
                  multiple
                  value={activityTypes}
                  onChange={(e) =>
                    setActivityTypes(e.target.value as ActivityType[])
                  }
                  renderValue={(selected) =>
                    selected.length
                      ? selected
                          .map((v) => t(`sessions.types.${v}`))
                          .join(", ")
                      : t("patientManage.export.allActivityTypes")
                  }
                >
                  {ACTIVITY_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`sessions.types.${type}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <FormControlLabel
            disabled
            control={<Checkbox checked />}
            label={t("patientManage.export.selectedCount", {
              count: patientIds.length,
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("patientManage.export.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={loading || !from || !to}
        >
          {loading
            ? t("patientManage.export.exporting")
            : t("patientManage.export.download")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientExportDialog;
